<script setup lang="ts">
import { ToastNotificationTypes } from "../composables/useToastNotifications";
import IconInfo from "../components/Icon/Info.vue";
import IconSuccess from "../components/Icon/Success.vue";
import IconSuccessGradient from "../components/Icon/SuccessGradient.vue";
import { launchConfetti } from "@/utils/confetti";
import { DynamicLabel, useDyanmicI18n } from "@/composables/useDynamicI18n";

const { dt } = useDyanmicI18n();

type ToastAppearance = {
  classes: string[];
  title: DynamicLabel;
  icon: typeof IconInfo | typeof IconSuccess;
  color: string;
};

export type ToastNotificationProps = {
  type?: ToastNotificationTypes;
  title?: DynamicLabel;
  label: DynamicLabel;
  index?: number;
  duration?: number;
};

const props = withDefaults(defineProps<ToastNotificationProps>(), {
  type: ToastNotificationTypes.INFORMATION,
  index: 0,
  duration: 5000,
});

const emit = defineEmits(["expired"]);

const appearanceDictionary: Record<number, ToastAppearance> = {
  [ToastNotificationTypes.INFORMATION]: {
    classes: ["text-ppa-dark-blue"],
    title: { i18n: "general.notifications.information" },
    icon: IconInfo,
    color: "#042B5F",
  },
  [ToastNotificationTypes.SUCCESS]: {
    classes: ["text-ppa-green"],
    title: { i18n: "general.notifications.success" },
    icon: IconSuccess,
    color: "#0F8577",
  },
  [ToastNotificationTypes.ERROR]: {
    classes: ["text-ppa-red"],
    title: { i18n: "general.notifications.error" },
    icon: IconInfo,
    color: "#E61E4D",
  },
  [ToastNotificationTypes.REWARD]: {
    classes: [
      "bg-gradient-to-r from-ppa-logo-blue via-ppa-logo-green to-ppa-logo-purple text-transparent bg-clip-text",
    ],
    title: props.title ?? { i18n: "general.notifications.success" },
    icon: IconSuccessGradient,
    color: "",
  },
};

const toastAppearance = computed(() => {
  return appearanceDictionary[props.type];
});

const translatedTo = ref("0px");
const transitionDelay = ref("0s");

const isExpired = ref(false);

watch(
  () => props.index,
  () => {
    translatedTo.value = `${props.index * 100}%`;
  },
);

const onTransitionEnd = () => {
  if (isExpired.value) {
    emit("expired", props.index);
  }
};

const cancelToast = () => {
  isExpired.value = true;
  cancelAnimationFrame(animationFrame);
};

let animationFrame: number;

const startTimer = (options: { end: number; run: Function }, callback: Function) => {
  let end = options.end;
  let run = options.run;
  const step = (now: number) => {
    const remaining = end - now;
    if (remaining <= 0) {
      run(0);
      if (callback) {
        callback();
      }
      return;
    } else {
      run(remaining);
    }
    animationFrame = requestAnimationFrame(step);
  };
  animationFrame = requestAnimationFrame(step);
};

onBeforeUnmount(() => {
  window.cancelAnimationFrame(animationFrame);
});

onMounted(() => {
  if (props.type === ToastNotificationTypes.REWARD) {
    launchConfetti();
  }
  startTimer(
    {
      end: window.performance.now() + props.duration,
      run: () => {},
    },
    () => {
      isExpired.value = true;
    },
  );
});
</script>

<template>
  <div
    class="toast-notification absolute right-6 top-4 z-100 select-none py-2"
    :style="{ '--delay': transitionDelay, '--translated-to': translatedTo }"
    :class="{ 'toast-notification--expired': isExpired }"
    @transitionend="onTransitionEnd()"
  >
    <div
      class="relative flex min-h-[100px] w-[440px] flex-col rounded-lg bg-ppa-white p-4 shadow-notification-shadow"
    >
      <button
        class="absolute right-4 top-[18px] z-100 cursor-pointer"
        :aria-label="$t('general.close')"
        @click="cancelToast()"
      >
        <IconClose color="#586679" />
      </button>
      <div class="flex flex-auto flex-col" role="status">
        <div class="flex items-center" style="transform: translateZ(0)">
          <component
            class="mr-4 h-6 w-6"
            aria-hidden="true"
            role="none"
            :is="toastAppearance.icon"
            :color="toastAppearance.color"
          >
          </component>
          <div
            class="flex-auto text-left text-ppa-notification-title font-bold"
            :class="toastAppearance.classes"
          >
            {{ dt(toastAppearance.title) }}
          </div>
        </div>
        <div class="relative flex flex-auto pt-2 text-ppa-default font-medium text-ppa-black">
          <img
            v-if="type === ToastNotificationTypes.REWARD"
            src="/public/icons/banana-cat.svg"
            alt=""
            class="animated-icon l-0 absolute h-10 w-10"
          />
          <div class="ml-10">
            {{ dt(label) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toast-notification {
  animation: slideTopDown 0.3s ease-in-out backwards;
  transform: translate3d(0, var(--translated-to), 0);
  transition: transform 0.3s ease-in-out;
  transition-delay: var(--delay);
}

.toast-notification--expired {
  transform: translate3d(100%, var(--translated-to), 0);
}

@keyframes slideTopDown {
  from {
    transform: translate3d(0, -100%, 0);
  }
  to {
    transform: translate3d(0, 0, 0);
  }
}

.animated-icon {
  animation: icon 1s ease-in-out infinite;
}

@keyframes icon {
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}
</style>
